import {z} from "zod"
import {useSuspenseQuery} from "@tanstack/react-query"

import {getJson} from "../http"

const SettlementInFullPropertiesSchema = z.object({
	DiscountPercentage: z.string(),
	DiscountValue: z.number(),
	ExpireDate: z.string(),
	OutstandingBalanceWithDiscount: z.number(),
	RequiresAffordabilityAssessment: z.boolean(),
})

const PaymentInFullSchema = z.object({
	Type: z.literal("PaymentInFull"),
	Order: z.number(),
	Properties: z.object({
		RequiresAffordabilityAssessment: z.boolean(),
		ShowDisclaimer: z.boolean(),
	}),
})

const PaymentPlanSchema = z.object({
	Type: z.literal("PaymentPlan"),
	Order: z.number(),
	Properties: z.object({
		RequiresAffordabilityAssessment: z.boolean(),
	}),
})

const PartialPaymentSchema = z.object({
	Type: z.literal("PartialPayment"),
	Order: z.number(),
	Properties: z.object({
		RequiresAffordabilityAssessment: z.boolean(),
	}),
})

const PaymentArrangementSchema = z.object({
	Type: z.literal("PaymentArrangement"),
	Order: z.number(),
	Properties: z.object({
		RequiresAffordabilityAssessment: z.boolean(),
	}),
})

const PaymentArrangementInFullSchema = z.object({
	Type: z.literal("PaymentArrangementInFull"),
	Order: z.number(),
	Properties: z.object({
		RequiresAffordabilityAssessment: z.boolean(),
	}),
})

const PromiseToPaySchema = z.object({
	Type: z.literal("PromiseToPay"),
	Order: z.number(),
	Properties: z.object({
		RequiresAffordabilityAssessment: z.boolean(),
	}),
})

const SettlementInFullSchema = z.object({
	Type: z.literal("SettlementInFull"),
	Order: z.number(),
	Properties: SettlementInFullPropertiesSchema,
})

const SettlementPlanPropertiesSchema = z.object({
	DiscountPercentage: z.string(),
	DiscountValue: z.number(),
	EndDate: z.string(),
	OutstandingBalanceWithDiscount: z.number(),
	RequiresAffordabilityAssessment: z.boolean(),
	StartDate: z.string(),
})

const SettlementPlanSchema = z.object({
	Type: z.literal("SettlementPlan"),
	Order: z.number(),
	Properties: SettlementPlanPropertiesSchema,
})

const OptionSchema = z.discriminatedUnion("Type", [
	SettlementInFullSchema,
	SettlementPlanSchema,
	PaymentInFullSchema,
	PaymentPlanSchema,
	PartialPaymentSchema,
	PaymentArrangementSchema,
	PaymentArrangementInFullSchema,
	PromiseToPaySchema,
])

// Payload schema
const PayloadSchema = z.object({
	RequiresAffordabilityAssessment: z.boolean(),
	PaymentOptions: z.array(OptionSchema),
	StatuteOfLimitationsDisclosure: z.string(),
	DisplayLicenceNumberText: z.boolean(),
	LicenceNumber: z.string(),
	CustomerName: z.string(),
	ClientName: z.string(),
	OutstandingBalance: z.number(),
	Currency: z.string(),
	ShowFooter: z.boolean(),
})

// Main response schema
const CustomerFindOutstandingBalanceSchema = z.object({
	ID: z.string(),
	Status: z.literal("OutstandingBalance"),
	Payload: PayloadSchema,
	I18n: z.null().or(z.any()), // Can be null or any other type
})

export type CustomerFindOutstandingBalanceResponse = z.infer<typeof CustomerFindOutstandingBalanceSchema>

const CustomerFindPaymentBeingProcessedSchema = z.object({
	Status: z.literal("PaymentBeingProcessed"),
	Payload: z.object({
		ClientName: z.string(),
		CustomerName: z.string(),
		PaymentAmountCurrency: z.string(),
		PaymentAmountValue: z.number(),
	}),
	I18n: z.null().or(z.any()), // Can be null or any other type
})

const CustomerFindLowOutstandingBalanceSchema = z.object({
	Status: z.literal("LowOutstandingBalance"),
	Payload: z.object({}),
})

const CustomerFindSettledAllAccountsSchema = z.object({
	Status: z.literal("SettledAllAccounts"),
	Payload: z.object({
		ClientName: z.string(),
		CustomerName: z.string(),
	}),
})

const CustomerFindInPaymentPlanSchema = z.object({
	Status: z.literal("InPaymentPlan"),
	Payload: z.object({
		PaymentPlanID: z.string(),
	}),
})

const CustomerFindInPaymentArrangementSchema = z.object({
	Status: z.literal("InPaymentArrangement"),
	Payload: z.object({
		PaymentArrangementID: z.string(),
		Next: z.string(),
	}),
})

const CustomerFindDisputeSchema = z.object({
	Status: z.literal("Dispute"),
	Payload: z.object({
		ClientName: z.string(),
	}),
})

const CustomerFindPaymentsDisabledSchema = z.object({
	Status: z.literal("PaymentsDisabled"),
	Payload: z.object({}),
})

const CustomerFindRecalledSchema = z.object({
	Status: z.literal("Recalled"),
	Payload: z.object({
		ClientName: z.string(),
	}),
})

const CustomerFindCaseEscalatedSchema = z.object({
	Status: z.literal("CaseEscalated"),
	Payload: z.object({
		ClientName: z.string(),
	}),
})

const CustomerFindNotificationOfAssignmentSchema = z.object({
	Status: z.literal("NotificationOfAssignment"),
	Payload: z.object({
		ClientName: z.string(),
	}),
})

const CustomerFindScrubDetectedSchema = z.object({
	Status: z.literal("ScrubDetected"),
})

const CustomerFindAccountsClosedDueToSOLSchema = z.object({
	Status: z.literal("AccountsClosedDueToSOL"),
	Payload: z.object({
		ClientName: z.string(),
	}),
})

const CustomerFindPendingScrubSchema = z.object({
	Status: z.literal("PendingScrub"),
})

const CustomerFindResponseSchema = z.discriminatedUnion("Status", [
	CustomerFindOutstandingBalanceSchema,
	CustomerFindPaymentBeingProcessedSchema,
	CustomerFindLowOutstandingBalanceSchema,
	CustomerFindSettledAllAccountsSchema,
	CustomerFindInPaymentPlanSchema,
	CustomerFindInPaymentArrangementSchema,
	CustomerFindDisputeSchema,
	CustomerFindPaymentsDisabledSchema,
	CustomerFindRecalledSchema,
	CustomerFindCaseEscalatedSchema,
	CustomerFindNotificationOfAssignmentSchema,
	CustomerFindScrubDetectedSchema,
	CustomerFindAccountsClosedDueToSOLSchema,
	CustomerFindPendingScrubSchema,
])

type CustomerFindResponse = z.infer<typeof CustomerFindResponseSchema>
export type CustomerFindPayload = z.infer<typeof PayloadSchema>

function customerFind({secureCode}: {secureCode: string}): Promise<CustomerFindResponse> {
	return getJson(`/app/customers/${secureCode}`, CustomerFindResponseSchema, {})
}

export function useCustomerFindQuery(secureCode: string) {
	return useSuspenseQuery({
		queryKey: ["customerFind", secureCode],
		queryFn: () => customerFind({secureCode}),
	})
}

export {customerFind}
export type {CustomerFindResponse}
